/**
 * Neue Richtlinie aus einer Word-Datei oder einem getaggten PDF: Datei wählen, Probelauf zeigt, was erkannt wird
 * (Abschnitte, Tabellen, Abbildungen, Fußnoten), dann anlegen und öffnen. Abschnitte entstehen aus
 * den Überschriften der Datei.
 */
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileUp, Upload } from "lucide-react";
import { toast } from "sonner";

import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { useGuidelinesAPI, type DocxImportResult } from "./GuidelinesAPI";

const ACCEPT = ".docx,.pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf";

export function ImportDialog(props: { customer: string; open: boolean; onOpenChange: (open: boolean) => void; onCreated: (documentId: string) => void }) {
  const { t } = useTranslation();
  const api = useGuidelinesAPI(props.customer);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [preview, setPreview] = useState<DocxImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setFile(null);
    setTitle("");
    setPreview(null);
  };

  const choose = async (f: File | null) => {
    setFile(f);
    setPreview(null);
    if (!f) return;
    setBusy(true);
    try {
      const r = await api.importDocx(f, false);
      setPreview(r);
      if (r.title && !title) setTitle(r.title);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const create = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const r = await api.importDocx(file, true, title.trim() || undefined);
      toast.success(t("guidelinesPage.import.created", { title: r.title, count: r.sections }));
      props.onOpenChange(false);
      reset();
      props.onCreated(r.documentId);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={props.open}
      onOpenChange={(open) => {
        if (!open) reset();
        props.onOpenChange(open);
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("guidelinesPage.import.title")}</DialogTitle>
          <DialogDescription>{t("guidelinesPage.import.intro")}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 text-sm">
          {/* Eigener Knopf statt des nativen Felds — dessen „Choose file / No file chosen“ folgt der Browsersprache, nicht dem Portal. */}
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => {
              choose(e.target.files?.[0] ?? null);
              // Geleert, damit dieselbe Datei noch einmal gewählt werden kann.
              e.target.value = "";
            }}
            data-testid="import-file"
          />
          <div
            className={`flex min-w-0 items-center gap-3 rounded-md border border-dashed p-3 ${dragOver ? "border-primary bg-muted" : "border-input"}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const dropped = e.dataTransfer.files?.[0];
              if (dropped) choose(dropped);
            }}
          >
            <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()}>
              <Upload size={14} className="mr-1" />
              {t("guidelinesPage.import.chooseFile")}
            </Button>
            <span className="min-w-0 flex-1 break-words text-muted-foreground" data-testid="import-file-name">
              {file?.name ?? t("guidelinesPage.import.noFile")}
            </span>
          </div>
          {busy && !preview ? <p className="text-muted-foreground">{t("guidelinesPage.import.reading")}</p> : null}
          {preview ? (
            <>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-muted-foreground">{t("guidelinesPage.import.name")}</span>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} />
              </label>
              <p data-testid="import-preview">
                {t("guidelinesPage.import.preview", {
                  sections: preview.sections,
                  tables: preview.stats.tables,
                  figures: preview.stats.figures,
                  footnotes: preview.stats.footnotes,
                  lists: preview.stats.lists,
                })}
              </p>
              {!preview.sections ? <p className="text-destructive">{t("guidelinesPage.import.noHeadings")}</p> : null}
            </>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => props.onOpenChange(false)}>
            {t("guidelinesPage.cancel")}
          </Button>
          <Button onClick={create} disabled={!preview?.sections || busy}>
            <FileUp size={14} className="mr-1" />
            {t("guidelinesPage.import.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
